import React, { useState } from 'react';
import { Modal, Heading, Text } from './ui';

const PausedSessionModal = ({ 
    session, 
    onResume, 
    onRestart, 
    onCancel 
}) => {
    const [showRestartWarning, setShowRestartWarning] = useState(false);
    const [isResuming, setIsResuming] = useState(false);
    const [isRestarting, setIsRestarting] = useState(false);

    const colorTokens = {
        oxfordBlue: '#1b263b',
        neutral: {
            0: '#FFFFFF',
            50: '#F9F8F6',
            900: '#0D0D0D'
        }
    };

    // Calculate session statistics
    const getSessionStats = () => {
        const stats = {
            currentStep: session.current_step || 0,
            totalSteps: session.total_steps || 0,
            photosCount: 0,
            checkInsCount: 0,
            timeElapsed: 0,
            timeRemaining: session.time_remaining || 0
        };

        // Parse session report to get statistics
        if (session.session_report) {
            const report = typeof session.session_report === 'string' 
                ? JSON.parse(session.session_report) 
                : session.session_report;

            if (report.timeline && Array.isArray(report.timeline)) {
                report.timeline.forEach(entry => {
                    if (entry.type === 'photo') stats.photosCount++;
                    if (entry.type === 'check_in') stats.checkInsCount++;
                });
            }
        }

        // Calculate time elapsed
        if (session.started_at) {
            const start = new Date(session.started_at);
            const end = session.paused_at ? new Date(session.paused_at) : new Date();
            stats.timeElapsed = Math.floor((end - start) / 1000 / 60); // minutes
        }

        return stats;
    };

    const stats = getSessionStats();

    const handleResume = async () => {
        setIsResuming(true);
        try {
            await onResume();
        } finally {
            setIsResuming(false);
        }
    };

    const handleRestart = async () => {
        setIsRestarting(true);
        try {
            await onRestart();
        } finally {
            setIsRestarting(false);
        }
    };

    if (showRestartWarning) {
        return (
            <Modal onClose={() => setShowRestartWarning(false)} maxWidth="max-w-2xl">
                <div className="minimal-padding">
                    <div className="flex items-center justify-between mb-6">
                        <Heading level={2} style={{ color: colorTokens.oxfordBlue }}>
                            Confirm Restart
                        </Heading>
                        <button
                            onClick={() => setShowRestartWarning(false)}
                            className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="bg-neutral-50 p-4 hairline mb-6">
                        <Text className="text-neutral-900 font-medium mb-2">
                            ⚠️ This will delete your current progress
                        </Text>
                        <Text className="text-neutral-900 opacity-60">
                            You'll lose all photos, check-ins, and progress from this session. 
                            This action cannot be undone.
                        </Text>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={handleRestart}
                            disabled={isRestarting}
                            className="flex-1 px-4 py-2 bg-red-600 text-white font-mono text-sm uppercase tracking-wider hover:opacity-60 disabled:opacity-50"
                        >
                            {isRestarting ? 'Restarting...' : 'Yes, Start Fresh'}
                        </button>
                        <button
                            onClick={() => setShowRestartWarning(false)}
                            disabled={isRestarting}
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal onClose={onCancel} maxWidth="max-w-4xl">
            <div className="minimal-padding">
                <div className="flex items-center justify-between mb-6">
                    <Heading level={2} style={{ color: colorTokens.oxfordBlue }}>
                        🍳 Paused Cooking Session
                    </Heading>
                    <button
                        onClick={onCancel}
                        className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close"
                    >
                        ✕
                    </button>
                </div>

                <div className="mb-6">
                    <Text size="lg" className="text-neutral-900 font-medium mb-4">
                        {session.recipe_title}
                    </Text>
                    <Text className="text-neutral-900 opacity-60">
                        Your cooking session was paused. You can continue where you left off with all your progress, 
                        photos, and tips intact, or start fresh with a new session.
                    </Text>
                </div>

                {/* Enhanced Session Progress Summary */}
                <div className="bg-neutral-50 p-6 hairline mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div className="text-center">
                            <div className="text-2xl font-mono font-medium" style={{ color: colorTokens.oxfordBlue }}>
                                {stats.currentStep + 1}/{stats.totalSteps}
                            </div>
                            <Text className="text-sm text-neutral-900 opacity-60">Steps</Text>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-mono font-medium" style={{ color: colorTokens.oxfordBlue }}>
                                {stats.timeElapsed}m
                            </div>
                            <Text className="text-sm text-neutral-900 opacity-60">Time Elapsed</Text>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-mono font-medium" style={{ color: colorTokens.oxfordBlue }}>
                                {stats.photosCount}
                            </div>
                            <Text className="text-sm text-neutral-900 opacity-60">Photos</Text>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-mono font-medium" style={{ color: colorTokens.oxfordBlue }}>
                                {stats.checkInsCount}
                            </div>
                            <Text className="text-sm text-neutral-900 opacity-60">Check-ins</Text>
                        </div>
                    </div>
                    
                    {stats.timeRemaining > 0 && (
                        <div className="bg-blue-50 p-3 hairline">
                            <Text className="text-sm text-blue-900">
                                ⏱️ Timer paused with {Math.floor(stats.timeRemaining / 60)}m {stats.timeRemaining % 60}s remaining
                            </Text>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={handleResume}
                        disabled={isResuming || isRestarting}
                        className="flex-1 px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all"
                        style={{ 
                            backgroundColor: colorTokens.oxfordBlue,
                            color: '#FFFFFF'
                        }}
                    >
                        {isResuming ? 'Resuming...' : '▶️ Continue Cooking'}
                    </button>
                    <button
                        onClick={() => setShowRestartWarning(true)}
                        disabled={isResuming || isRestarting}
                        className="px-6 py-3 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 disabled:opacity-50"
                    >
                        🔄 Start Fresh
                    </button>
                </div>

                {/* Additional Info */}
                <div className="mt-6 p-4 bg-blue-50 hairline">
                    <Text className="text-sm text-blue-900">
                        💡 <strong>Continue:</strong> Pick up exactly where you left off with all your progress, photos, and AI tips.
                    </Text>
                    <Text className="text-sm text-blue-900 mt-2">
                        🔄 <strong>Start Fresh:</strong> Begin a new session from step 1 with new AI tips and reminders.
                    </Text>
                </div>
            </div>
        </Modal>
    );
};

export default PausedSessionModal;

